import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { MAIN_URL, getApiHeaders } from "../../Configurations/Urls";
import useStudentPortalFetch, {
  StudentPortalShell,
  StudentPortalState,
  formatPortalDate,
  formatPortalTime,
} from "./useStudentPortalFetch";
import useStudentCourseSelection from "./useStudentCourseSelection";
import StudentCourseFilter from "./StudentCourseFilter";

function formatMarks(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  if (Number.isInteger(n) || Math.abs(n - Math.round(n)) < 1e-9) {
    return String(Math.round(n));
  }
  return String(parseFloat(n.toFixed(2)));
}

function formatScore(obtained, maxScore) {
  const score = formatMarks(obtained);
  if (score == null) return "—";
  const max = formatMarks(maxScore);
  if (max == null) return score;
  return `${score} / ${max}`;
}

function scorePercentage(obtained, maxScore, percentage) {
  if (percentage != null && percentage !== "") {
    const n = Number(percentage);
    if (!Number.isNaN(n)) return formatMarks(Math.round(n * 100) / 100);
  }
  const score = Number(obtained);
  const max = Number(maxScore);
  if (!Number.isNaN(score) && !Number.isNaN(max) && max > 0) {
    return formatMarks(Math.round((score / max) * 10000) / 100);
  }
  return null;
}

function gradeFromPercentage(percentage) {
  const pct = Number(percentage);
  if (Number.isNaN(pct)) return null;
  if (pct >= 90) return "A+";
  if (pct >= 80) return "A";
  if (pct >= 70) return "B+";
  if (pct >= 60) return "B";
  if (pct >= 50) return "C";
  if (pct >= 40) return "D";
  return "F";
}

function FileLink({ href, children }) {
  if (!href) return <span>—</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="ems-student-link"
    >
      {children}
    </a>
  );
}

function rowKey(row, index) {
  return (
    row.organization_ems_assessment_schedule_student_id ||
    row.organization_ems_assessment_id ||
    index
  );
}

function parseScheduleDateTime(dateValue, timeValue, fallbackTime = "00:00:00") {
  if (!dateValue) return null;
  const raw = String(dateValue).trim();
  const iso = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const dmy = raw.match(/^(\d{2})[-/](\d{2})[-/](\d{4})/);
  const date = iso
    ? `${iso[1]}-${iso[2]}-${iso[3]}`
    : dmy
      ? `${dmy[3]}-${dmy[2]}-${dmy[1]}`
      : raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return null;
  if (!timeValue && fallbackTime == null) return null;
  let time = timeValue ? String(timeValue).trim() : fallbackTime;
  if (/^\d{2}:\d{2}$/.test(time)) time = `${time}:00`;
  if (!/^\d{2}:\d{2}:\d{2}/.test(time)) {
    if (fallbackTime == null) return null;
    time = fallbackTime;
  }
  const parsed = new Date(`${date}T${String(time).slice(0, 8)}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function laterDate(a, b) {
  if (!a) return b || null;
  if (!b) return a;
  return a.getTime() >= b.getTime() ? a : b;
}

function assignmentWindowState(row) {
  const now = Date.now();
  // Prefer absolute ISO windows from API (already converted with offset).
  const startFromApi = row?.window_starts_at ? new Date(row.window_starts_at) : null;
  const endFromApi = row?.window_ends_at ? new Date(row.window_ends_at) : null;
  const apiStart =
    startFromApi && !Number.isNaN(startFromApi.getTime()) ? startFromApi : null;
  const apiEnd =
    endFromApi && !Number.isNaN(endFromApi.getTime()) ? endFromApi : null;

  const start =
    apiStart ||
    parseScheduleDateTime(row?.assessment_date, row?.start_time, "00:00:00");

  const sessionEnd = parseScheduleDateTime(
    row?.assessment_date,
    row?.end_time,
    null
  );
  const dueEnd = parseScheduleDateTime(
    row?.due_date || row?.assessment_date,
    row?.due_time || row?.end_time,
    "23:59:59"
  );
  const end = apiEnd || laterDate(sessionEnd, dueEnd);

  const notStarted = Boolean(start && now < start.getTime());
  const ended = Boolean(end && now > end.getTime());
  // Backend window_open is authoritative when present (institute-TZ window math).
  const open =
    typeof row?.window_open === "boolean"
      ? Boolean(row.window_open)
      : !notStarted && !ended;

  return { start, end, notStarted, ended, open };
}

function windowStatusLabel(windowState) {
  if (windowState.notStarted) {
    const start = windowState.start;
    if (start) {
      const time = formatPortalTime(
        `${start.getHours()}:${String(start.getMinutes()).padStart(2, "0")}`
      );
      return `Starts at ${time}`;
    }
    return "Not started yet";
  }
  if (windowState.ended) {
    return "Window closed";
  }
  return "";
}

function formatDateTimeParts(dateValue, timeValue, timezoneLabel = "") {
  const date = formatPortalDate(dateValue);
  const time = formatPortalTime(timeValue, timezoneLabel);
  if (date && time) return `${date} ${time}`;
  return date || time || "";
}

function scheduleTimingMeta(row, timezoneLabel = "") {
  const tz = row?.timezone_label || timezoneLabel || "";
  const dueLabel = formatDateTimeParts(row?.due_date, row?.due_time, tz);
  return { dueLabel };
}

function hasPublishedResult(row) {
  const status = String(row?.result_status || "").trim().toLowerCase();
  const hasScore =
    row?.score_obtained != null && row?.score_obtained !== "";
  if (!status || status === "pending" || status === "unpublished") {
    return hasScore;
  }
  return true;
}

function studentStatusLabel(row) {
  const attempt = String(row?.attempt_status || "");
  if (attempt === "Submitted" || attempt === "Auto Submitted") {
    return "Submitted";
  }
  if (row?.submission_url) return "Submitted";
  if (hasPublishedResult(row)) return "Submitted";
  return "Unpublished";
}

function resultDisplay(row) {
  if (!hasPublishedResult(row)) {
    return { unpublished: true };
  }
  const score = formatScore(row.score_obtained, row.max_score);
  const pct = scorePercentage(
    row.score_obtained,
    row.max_score,
    row.percentage
  );
  return {
    unpublished: false,
    score,
    percentage: pct,
    grade: row.grade || gradeFromPercentage(pct),
  };
}

function hasNoteText(value) {
  const text = String(value ?? "").trim();
  return text !== "" && text.toLowerCase() !== "null" && text !== "-";
}

function assignmentGuidance(row) {
  if (!row) return { instructions: "", remarks: "", referenceUrl: "" };
  return {
    instructions: hasNoteText(row.instructions)
      ? String(row.instructions).trim()
      : "",
    remarks: hasNoteText(row.remarks) ? String(row.remarks).trim() : "",
    referenceUrl: hasNoteText(row.reference_url)
      ? String(row.reference_url).trim()
      : "",
  };
}

export default function StudentAssignmentsPage() {
  const { data, meta, loading, error, refetch } =
    useStudentPortalFetch("assignments");
  const {
    options,
    selectedAdmissionId,
    setSelectedAdmissionId,
    showFilter,
    loading: coursesLoading,
    filterRows,
  } = useStudentCourseSelection();
  const rows = filterRows(Array.isArray(data) ? data : []);
  const timezoneLabel =
    meta?.timezone_label ||
    rows[0]?.timezone_label ||
    "";
  const [uploadingId, setUploadingId] = useState(null);
  const [uploadModalRow, setUploadModalRow] = useState(null);
  const [uploadAck, setUploadAck] = useState(false);

  const closeUploadModal = () => {
    setUploadModalRow(null);
    setUploadAck(false);
  };

  const openUploadModal = (row) => {
    const windowState = assignmentWindowState(row);
    if (!windowState.open) {
      toast.error(
        windowStatusLabel(windowState) || "This assignment is not open yet."
      );
      return;
    }
    setUploadModalRow(row);
    setUploadAck(false);
  };

  const handleUpload = async (row, file) => {
    const targetId =
      row.organization_ems_assessment_schedule_student_id ||
      row.organization_ems_assessment_id;
    if (!file || !targetId) return;

    const windowState = assignmentWindowState(row);
    if (!windowState.open) {
      toast.error(
        windowStatusLabel(windowState) || "This assignment is not open yet."
      );
      return;
    }

    setUploadingId(targetId);
    try {
      const headers = getApiHeaders();
      delete headers["Content-Type"];
      delete headers["content-type"];

      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        `${MAIN_URL}/api/ems/portal/assignments/${targetId}/submit`,
        formData,
        { headers }
      );

      toast.success(res?.data?.message || "Assignment submitted.");
      closeUploadModal();
      await refetch();
    } catch (err) {
      const apiErrors = err?.response?.data?.errors;
      const firstError =
        apiErrors && typeof apiErrors === "object"
          ? Object.values(apiErrors).flat()?.[0]
          : null;
      toast.error(
        firstError ||
          err?.response?.data?.message ||
          err?.message ||
          "Could not upload assignment."
      );
    } finally {
      setUploadingId(null);
    }
  };

  const modalGuidance = assignmentGuidance(uploadModalRow);
  const modalHasGuidance = Boolean(
    modalGuidance.instructions ||
      modalGuidance.remarks ||
      modalGuidance.referenceUrl
  );
  const modalBusy =
    uploadModalRow &&
    uploadingId ===
      (uploadModalRow.organization_ems_assessment_schedule_student_id ||
        uploadModalRow.organization_ems_assessment_id);
  const canModalUpload = !modalBusy && (!modalHasGuidance || uploadAck);

  return (
    <StudentPortalShell
      title="My Assessments"
      subtitle={
        timezoneLabel ? `All times shown in ${timezoneLabel}` : undefined
      }
      headerRight={
        <StudentCourseFilter
          options={options}
          value={selectedAdmissionId}
          onChange={setSelectedAdmissionId}
          showFilter={showFilter}
          loading={coursesLoading}
        />
      }
    >
      <StudentPortalState
        loading={loading}
        error={error}
        empty={!loading && !error && rows.length === 0}
      >
        <section className="ems-student-section">
          <div className="ems-student-table-wrap ems-assessment-table-wrap">
            <table className="ems-student-table ems-assessment-table">
              <thead>
                <tr>
                  <th>Assessment</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Result</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => {
                  const id = rowKey(row, index);
                  const mode = row.delivery_mode || "Submission";
                  const busy =
                    uploadingId ===
                    (row.organization_ems_assessment_schedule_student_id ||
                      row.organization_ems_assessment_id);
                  const windowState = assignmentWindowState(row);
                  const alreadyAttempted =
                    row.attempt_status === "Submitted" ||
                    row.attempt_status === "Auto Submitted";
                  const canStartTest =
                    alreadyAttempted ||
                    (Boolean(
                      row.organization_ems_assessment_schedule_student_id
                    ) &&
                      windowState.open &&
                      Boolean(row.organization_ems_test_id));
                  const canUpload =
                    Boolean(
                      row.organization_ems_assessment_schedule_student_id
                    ) &&
                    windowState.open &&
                    !busy;
                  const scheduledLabel = [
                    formatPortalDate(row.assessment_date),
                    row.start_time
                      ? formatPortalTime(
                          row.start_time,
                          row.timezone_label || timezoneLabel
                        )
                      : null,
                  ]
                    .filter(Boolean)
                    .join(" ");
                  const { dueLabel } = scheduleTimingMeta(row, timezoneLabel);
                  const statusLabel = studentStatusLabel(row);
                  const result = resultDisplay(row);
                  const windowClosed = windowState.ended;
                  const typeLabel = row.assessment_type || "—";

                  let actionControl = null;
                  if (mode === "Online") {
                    if (!row.organization_ems_assessment_schedule_student_id) {
                      actionControl = (
                        <button
                          type="button"
                          className="ems-student-btn ems-student-btn-sm ems-assessment-btn-closed"
                          disabled
                        >
                          Closed
                        </button>
                      );
                    } else if (alreadyAttempted) {
                      actionControl = (
                        <Link
                          className="ems-student-btn ems-student-btn-primary ems-student-btn-sm"
                          to={`/student/assessments/${row.organization_ems_assessment_schedule_student_id}/test`}
                        >
                          View test
                        </Link>
                      );
                    } else if (canStartTest) {
                      actionControl = (
                        <Link
                          className="ems-student-btn ems-student-btn-sm ems-assessment-btn-start"
                          to={`/student/assessments/${row.organization_ems_assessment_schedule_student_id}/test`}
                        >
                          Start Test
                        </Link>
                      );
                    } else {
                      actionControl = (
                        <button
                          type="button"
                          className="ems-student-btn ems-student-btn-sm ems-assessment-btn-closed"
                          disabled
                        >
                          {windowClosed ? "Closed" : "Start Test"}
                        </button>
                      );
                    }
                  } else if (mode === "In Person") {
                    actionControl = (
                      <FileLink href={row.attachment_url}>
                        {row.attachment_name || "Download paper"}
                      </FileLink>
                    );
                  } else if (!row.organization_ems_assessment_schedule_student_id) {
                    actionControl = (
                      <button
                        type="button"
                        className="ems-student-btn ems-student-btn-sm ems-assessment-btn-closed"
                        disabled
                      >
                        Closed
                      </button>
                    );
                  } else if (windowClosed && !row.submission_url) {
                    actionControl = (
                      <button
                        type="button"
                        className="ems-student-btn ems-student-btn-sm ems-assessment-btn-closed"
                        disabled
                      >
                        Closed
                      </button>
                    );
                  } else {
                    actionControl = (
                      <button
                        type="button"
                        className={`ems-student-btn ems-student-btn-sm${
                          canUpload
                            ? " ems-assessment-btn-start"
                            : " ems-assessment-btn-closed"
                        }${busy ? " is-busy" : ""}`}
                        disabled={!canUpload}
                        onClick={() => openUploadModal(row)}
                      >
                        {busy
                          ? "Uploading…"
                          : row.submission_url
                            ? "Replace"
                            : "Upload"}
                      </button>
                    );
                  }

                  return (
                    <tr key={id}>
                      <td className="ems-assessment-main-cell">
                        <div className="ems-assessment-main-title">
                          {row.assessment_name || "Assessment"}
                        </div>
                        {row.training_program_name ? (
                          <p className="ems-assessment-main-program">
                            {row.training_program_name}
                          </p>
                        ) : null}
                        <div className="ems-assessment-main-details">
                          <p className="ems-student-meta">
                            Scheduled Date: {scheduledLabel || "—"}
                          </p>
                          <p className="ems-student-meta">
                            Due Date: {dueLabel || "—"}
                          </p>
                          <p className="ems-student-meta">Mode: {mode}</p>
                          <p className="ems-student-meta ems-assessment-total-marks">
                            Total Marks:{" "}
                            {formatMarks(row.max_score ?? row.max_marks) || "—"}
                          </p>
                        </div>
                        <div className="ems-assessment-main-action">
                          {actionControl}
                        </div>
                      </td>
                      <td className="ems-assessment-type-cell" data-label="Type">
                        {typeLabel}
                      </td>
                      <td className="ems-assessment-status-cell" data-label="Status">
                        {statusLabel}
                      </td>
                      <td className="ems-assessment-result-cell" data-label="Result">
                        {result.unpublished ? (
                          <span className="ems-student-meta">Unpublished</span>
                        ) : (
                          <>
                            <div className="ems-assessment-score">
                              {result.score}
                            </div>
                            {result.percentage != null ? (
                              <div className="ems-student-meta">
                                {result.percentage}%
                              </div>
                            ) : null}
                            {result.grade ? (
                              <div className="ems-assessment-grade">
                                Grade {result.grade}
                              </div>
                            ) : null}
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      </StudentPortalState>

      {uploadModalRow ? (
        <div
          className="ems-student-modal-backdrop"
          role="presentation"
          onClick={closeUploadModal}
        >
          <div
            className="ems-student-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ems-upload-instructions-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="ems-student-modal-header">
              <h3 id="ems-upload-instructions-title">
                {uploadModalRow.assessment_name || "Assignment"}
              </h3>
              <button
                type="button"
                className="ems-student-modal-close"
                onClick={closeUploadModal}
                aria-label="Close"
              >
                ×
              </button>
            </div>

            <div className="ems-student-modal-body">
              {modalHasGuidance ? (
                <div className="ems-student-instructions-panel">
                  <h4 className="ems-student-instructions-title">Instructions</h4>
                  {modalGuidance.instructions ? (
                    <p className="ems-student-instructions-body">
                      {modalGuidance.instructions}
                    </p>
                  ) : null}
                  {modalGuidance.remarks ? (
                    <p className="ems-student-instructions-body">
                      {modalGuidance.instructions ? (
                        <strong>Remarks: </strong>
                      ) : null}
                      {modalGuidance.remarks}
                    </p>
                  ) : null}
                  {modalGuidance.referenceUrl ? (
                    <p className="ems-student-instructions-body">
                      <strong>Reference: </strong>
                      <a
                        href={
                          /^https?:\/\//i.test(modalGuidance.referenceUrl)
                            ? modalGuidance.referenceUrl
                            : `https://${modalGuidance.referenceUrl}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ems-student-link"
                      >
                        {modalGuidance.referenceUrl}
                      </a>
                    </p>
                  ) : null}
                </div>
              ) : (
                <p className="ems-student-meta">
                  No instructions provided for this assignment.
                </p>
              )}

              {modalHasGuidance ? (
                <label className="ems-student-instructions-ack">
                  <input
                    type="checkbox"
                    checked={uploadAck}
                    onChange={(e) => setUploadAck(e.target.checked)}
                  />
                  <span>I have read and understood the instructions</span>
                </label>
              ) : null}
            </div>

            <div className="ems-student-modal-footer">
              <button
                type="button"
                className="ems-student-btn ems-student-btn-secondary ems-student-btn-sm"
                onClick={closeUploadModal}
              >
                Cancel
              </button>
              <label
                className={`ems-student-btn ems-student-btn-primary ems-student-btn-sm${
                  modalBusy ? " is-busy" : ""
                }${canModalUpload ? "" : " is-disabled"}`}
              >
                {modalBusy
                  ? "Uploading…"
                  : uploadModalRow.submission_url
                    ? "Choose file to replace"
                    : "Choose file to upload"}
                <input
                  type="file"
                  hidden
                  disabled={!canModalUpload}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    e.target.value = "";
                    if (file) {
                      handleUpload(uploadModalRow, file);
                    }
                  }}
                />
              </label>
            </div>
          </div>
        </div>
      ) : null}
    </StudentPortalShell>
  );
}
